import { cn } from "../../../lib/utils.js";

export interface BarDatum {
  label: string;
  value: number;
  /** Per-bar fill class (identity coloring). Falls back to `defaultColorClass`. */
  colorClass?: string;
  /** Optional secondary caption shown under the label (e.g. a count). */
  sub?: string;
}

export interface BarChartProps {
  data: BarDatum[];
  formatValue: (n: number) => string;
  /** Fill class used when a datum has no `colorClass`. */
  defaultColorClass?: string;
  className?: string;
  emptyText?: string;
}

/** Ranked horizontal bars. Bar widths are SVG geometry (percentage of the rendered
 *  width), so no inline styles are needed; each bar carries a direct value label and
 *  a hover `<title>`. */
export function BarChart({
  data,
  formatValue,
  defaultColorClass = "fill-[var(--chart-1)]",
  className,
  emptyText = "No data",
}: BarChartProps) {
  const max = Math.max(1, ...data.map((d) => d.value));

  if (data.length === 0) {
    return <p className={cn("py-6 text-center text-[13px] text-surface-400", className)}>{emptyText}</p>;
  }

  return (
    <div className={cn("flex flex-col gap-2.5", className)}>
      {data.map((d) => {
        const pct = Math.max(d.value > 0 ? 1.5 : 0, (d.value / max) * 100); // keep tiny non-zero bars visible
        return (
          <div
            key={d.label}
            className="grid items-center gap-3 text-[13px] grid-cols-[6.5rem_1fr_auto] sm:grid-cols-[9.5rem_1fr_auto]"
          >
            <div className="min-w-0">
              <div className="truncate text-surface-700" title={d.label}>{d.label}</div>
              {d.sub && <div className="truncate text-[11px] text-surface-400">{d.sub}</div>}
            </div>
            <svg className="h-5 w-full overflow-visible" aria-hidden>
              <rect x={0} y={0} width="100%" height="100%" rx={4} className="fill-[var(--color-surface-100)]" />
              <rect x={0} y={0} width={`${pct}%`} height="100%" rx={4} className={d.colorClass ?? defaultColorClass}>
                <title>{`${d.label}: ${formatValue(d.value)}`}</title>
              </rect>
            </svg>
            <span className="shrink-0 font-semibold text-text-900 [font-variant-numeric:tabular-nums]">
              {formatValue(d.value)}
            </span>
          </div>
        );
      })}
    </div>
  );
}
